import type { VodChapter } from 'src/generated/graphql-manager';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { areChaptersInOrder, isValidTimeFormat } from './utils/chapter-helpers';

export type ChapterError = { id: VodChapter['id']; message: string };

/**
 * Custom hook to validate chapter offsets and format
 * @param chapters Current list of chapters
 * @returns List of validation errors
 */
export function useChapterValidation(chapters: VodChapter[]): ChapterError[] {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<ChapterError[]>([]);

  useEffect(() => {
    const newErrors: ChapterError[] = [];

    // Validate format for each chapter
    chapters.forEach((chapter) => {
      const offset = chapter.offsetInMs;

      // Check format: must be hh:mm:ss
      if (!isValidTimeFormat(offset)) {
        newErrors.push({
          id: chapter.id,
          message: t('manager.eventSummary.chapters.validation.invalidTimeFormat'),
        });
      }
    });

    // Check order only if all formats are valid
    if (newErrors.length === 0) {
      for (let i = 1; i < chapters.length; i++) {
        if (!areChaptersInOrder([chapters[i - 1], chapters[i]])) {
          newErrors.push({
            id: chapters[i].id,
            message: t('manager.eventSummary.chapters.validation.chapterOrderError'),
          });

          break; // Only report the first error to avoid overwhelming the user
        }
      }
    }

    setErrors(newErrors);
  }, [chapters, t]);

  return errors;
}
